<template>
  <ion-page>
    <ion-content>

      <div class="flex flex-col w-full bg-brand-black my-auto h-full px-6 pt-14 z-50">

        <div class="flex flex-col space-y-6">
          <div class="flex flex-col w-full items-center space-y-2">
            <div class="flex m-auto text-white text-2xl font-medium">Reset your password</div>
            <div class="m-auto text-brand-light-gray text-lg text-center">
              We emailed a 6-character code to
              <span class="text-brand-lime">{{ email }}</span>. Enter it below with your new password.
            </div>
          </div>

          <div class="flex flex-col space-y-3">
            <div class="flex w-full flex-col bg-brand-dark-gray rounded-lg h-14 relative">
              <ion-input
                  v-model="code"
                  class="text-white text-lg font-medium absolute top-0 left-0 h-full px-4"
                  placeholder="6-character code"
                  maxlength="6"
                  autocapitalize="characters"></ion-input>
            </div>
            <div class="flex w-full flex-col bg-brand-dark-gray rounded-lg h-14 relative">
              <ion-input
                  v-model="password"
                  class="text-white text-lg font-medium absolute top-0 left-0 h-full px-4"
                  placeholder="New password"
                  type="password"></ion-input>
            </div>
            <div class="flex w-full flex-col bg-brand-dark-gray rounded-lg h-14 relative">
              <ion-input
                  v-model="passwordConfirmation"
                  class="text-white text-lg font-medium absolute top-0 left-0 h-full px-4"
                  placeholder="Confirm password"
                  type="password"></ion-input>
            </div>
            <div v-if="error" class="text-red-400 text-sm px-1">{{ error }}</div>
          </div>
        </div>

        <button
            @click="submit"
            :disabled="busy || !canSubmit"
            class="mt-10 flex w-full bg-brand-lime rounded-full px-8 h-14 font-medium text-xl text-brand-black disabled:opacity-40">
          <div class="flex m-auto space-x-2.5">
            <div class="my-auto pt-0.5">
              <span v-if="!busy">Set new password</span>
              <svg v-else class="animate-spin h-5 w-5 text-brand-black" xmlns="http://www.w3.org/2000/svg" fill="none"
                   viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </div>
          </div>
        </button>

        <button
            @click="resend"
            :disabled="busy"
            class="mt-2 flex rounded-full w-full h-14 font-medium text-xl text-white disabled:opacity-40">
          <div class="flex m-auto space-x-2.5">
            <div class="my-auto pt-0.5">
              Resend code
            </div>
          </div>
        </button>

      </div>

    </ion-content>
  </ion-page>
</template>

<script lang="ts">
import {
  IonContent,
  IonPage,
  IonInput,
  toastController,
  useIonRouter,
} from '@ionic/vue';
import {computed, defineComponent, ref} from 'vue';
import {useRoute} from "vue-router";
import AuthService from "@/services/api/auth/authService";

export default defineComponent({
  name: 'ForgotPasswordInstructionsPage',
  components: {
    IonContent,
    IonPage,
    IonInput,
  },
  setup() {
    const route = useRoute()
    const ionRouter = useIonRouter()
    const authService = new AuthService()

    const email = ref(String(route.query.email || ''))
    const code = ref('')
    const password = ref('')
    const passwordConfirmation = ref('')
    const busy = ref(false)
    const error = ref('')

    const canSubmit = computed(() => {
      return email.value
          && code.value.trim().length === 6
          && password.value.length >= 6
          && password.value === passwordConfirmation.value
    })

    const submit = async () => {
      if (!canSubmit.value || busy.value) return
      busy.value = true
      error.value = ''
      try {
        await authService.verifyEmail(email.value, code.value.trim())
        await authService.changePassword(password.value, passwordConfirmation.value)
        await ionRouter.navigate('/onboarding', 'forward', 'replace')
      } catch (e: any) {
        error.value = e?.message || e?.data?.message || 'Could not reset password'
      } finally {
        busy.value = false
      }
    }

    const resend = async () => {
      if (!email.value || busy.value) return
      busy.value = true
      error.value = ''
      try {
        await authService.forgotPassword(email.value)
        const toast = await toastController.create({
          message: `A new code has been sent to ${email.value}`,
          duration: 3000,
          position: 'top',
          color: 'primary',
        })
        await toast.present()
      } catch (e: any) {
        error.value = e?.message || e?.data?.message || 'Could not resend code'
      } finally {
        busy.value = false
      }
    }

    return {
      email,
      code,
      password,
      passwordConfirmation,
      busy,
      error,
      canSubmit,
      submit,
      resend,
    }
  }
});
</script>
